import { useEffect, useRef } from "react";
import useChatStore, { EMPTY_ARRAY } from "../store/chatStore";
import { ChatCrypto } from "../utils/crypto";
import { getKeyBundle } from "../api/chatService";

/**
 * useMessageDecryption
 * 
 * Background hook that watches the message store and decrypts encrypted messages.
 */
export function useMessageDecryption(convId) {
  const messages = useChatStore((s) => s.messages[convId] ?? EMPTY_ARRAY);
  const conversation = useChatStore((s) => 
    s.conversations.find((c) => c.id === convId)
  );
  const aesKeyFromStore = useChatStore((s) => s.conversationKeys[convId]);
  const updateMessage = useChatStore((s) => s.updateMessage);
  const setConversationKey = useChatStore((s) => s.setConversationKey);

  const decryptingRef = useRef(new Set());

  useEffect(() => {
    if (!convId || !conversation) return;

    const processMessages = async () => {
      // 1. Get or Derive Conversation Key
      let aesKey = aesKeyFromStore;
      
      if (!aesKey && conversation.my_key) {
        try {
          console.log(`[Crypto] Deriving key for conversation ${convId}...`);
          const myKeyPair = await ChatCrypto.getIdentityKeyPair();
          const { encrypted_key, key_iv, hkdf_salt } = conversation.my_key;
          
          // 1. Get Other User's Public Key for this conversation
          // In a DM, we find the other member.
          const otherMember = (conversation.members || []).find(m => (m.employee_id || m.user_info?.employee_id) !== conversation.my_key.member_employee_id);
          const otherId = otherMember?.employee_id || otherMember?.user_info?.employee_id;
          
          if (otherId) {
            const { data: bundle } = await getKeyBundle(otherId);
            if (bundle && bundle.identity_public_key) {
              const theirPubKey = await ChatCrypto.importPublicKey(bundle.identity_public_key);
              
              // 2. Derive Shared Secret
              const sharedKey = await ChatCrypto.deriveSharedKey(myKeyPair.privateKey, theirPubKey, hkdf_salt);
              
              // 3. Decrypt Conversation Key
              const convKeyBase64 = await ChatCrypto.decrypt(encrypted_key, key_iv, sharedKey);
              if (convKeyBase64) {
                const convKeyRaw = ChatCrypto.base64ToArrayBuffer(convKeyBase64);
                aesKey = await window.crypto.subtle.importKey(
                  "raw",
                  convKeyRaw,
                  { name: "AES-GCM" },
                  true,
                  ["encrypt", "decrypt"]
                );
                setConversationKey(convId, aesKey);
                console.log(`[Crypto] Successfully derived key for conv ${convId}`);
              }
            }
          }
        } catch (err) {
          console.error(`[Crypto] Key derivation failed for ${convId}:`, err);
        }
      }

      // 2. Decrypt messages
      for (const msg of messages) {
        if (msg.ciphertext && !msg.decryptedContent && !decryptingRef.current.has(msg.id)) {
          if (!aesKey) continue; // Can't decrypt yet

          decryptingRef.current.add(msg.id);
          
          ChatCrypto.decrypt(msg.ciphertext, msg.iv, aesKey)
            .then((decrypted) => {
              if (decrypted) {
                updateMessage(convId, msg.id, { 
                  decryptedContent: decrypted,
                  content: decrypted // Sync content for UI if it was empty
                });
              }
            })
            .finally(() => {
              decryptingRef.current.delete(msg.id);
            });
        }
      }
    };

    processMessages();
  }, [convId, messages, conversation, aesKeyFromStore, updateMessage, setConversationKey]);
}
